import React, { useState } from 'react';
import { Alert, Box, Button, CircularProgress, Paper, TextField, Typography } from '@mui/material';
import { KeyRound } from 'lucide-react';
import { holdSessionKeys, unwrapWithRecoveryKey, RECOVERY_KEY_MISMATCH_MESSAGE } from '../../../services/chat/chatKeys';
import type { UnlockMethod } from '../../../services/chat/chatKeys';
import { PasskeyError, unlockWithPasskey } from '../../../services/chat/chatPasskeys';
import type { UserKeys } from '../../../services/chat/chatService';
import { trustBrowser } from '../../../services/chat/chatTrustedBrowser';
import { AddPasskeyDialog } from './AddPasskeyDialog';
import { IdentityResetDialog } from './IdentityResetDialog';

export const UNLOCK_WITH_PASSKEY_LABEL = 'Unlock with passkey';
export const USE_RECOVERY_KEY_LABEL = 'Use recovery key instead';
export const REPLACE_KEY_LABEL = 'Replace my chat encryption key';
export const PASSKEY_OFFER_TITLE = 'Add a passkey for this device?';
export const PASSKEY_OFFER_INTRO =
  'Next time, unlock chat on this device with a passkey touch instead of the recovery key.';

/** The once-per-browser offer flag: survives logout and the 30-day trust expiry. */
export function passkeyOfferStorageKey(uid: string): string {
  return `studysync-chat-passkey-offer:${uid}`;
}

export function passkeyOfferShown(uid: string): boolean {
  try {
    return localStorage.getItem(passkeyOfferStorageKey(uid)) != null;
  } catch {
    return true;
  }
}

export function markPasskeyOfferShown(uid: string): void {
  try {
    localStorage.setItem(passkeyOfferStorageKey(uid), new Date().toISOString());
  } catch {
    /* storage unavailable — the offer is simply not persisted */
  }
}

export interface UnlockGateProps {
  uid: string;
  email: string;
  keys: UserKeys;
  /** "Your chat key was replaced on <date>…" when applicable. */
  notice?: string | null;
  /** The session is held and this browser trusted; `keys` is the current key document. */
  onUnlocked: (keys: UserKeys) => void;
}

/**
 * Unlock on a new or expired browser: one primary button ("Unlock with
 * passkey"), one link ("Use recovery key instead"), and the low-emphasis
 * "Replace my chat encryption key" link that opens IdentityResetDialog.
 * There is no other way to unlock.
 */
export function UnlockGate({ uid, email, keys, notice, onUnlocked }: UnlockGateProps) {
  const [mode, setMode] = useState<'choose' | 'recovery'>('choose');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [recoveryInput, setRecoveryInput] = useState('');
  const [prfFailed, setPrfFailed] = useState(false);
  const [offerOpen, setOfferOpen] = useState(false);
  const [currentKeys, setCurrentKeys] = useState(keys);
  const [resetOpen, setResetOpen] = useState(false);

  const finishUnlock = async (privateKey: Uint8Array, method: UnlockMethod) => {
    holdSessionKeys(keys.publicKey, privateKey, method);
    await trustBrowser(uid, keys.publicKey, privateKey, keys.trustEpoch);
    const offer = (method === 'recovery' || (method === 'passkey' && prfFailed)) && !passkeyOfferShown(uid);
    if (offer) {
      markPasskeyOfferShown(uid);
      setOfferOpen(true);
      return;
    }
    onUnlocked(currentKeys);
  };

  const handlePasskey = async () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const { privateKey } = await unlockWithPasskey();
      await finishUnlock(privateKey, 'passkey');
    } catch (err: any) {
      if (err instanceof PasskeyError) {
        if (err.reason === 'prf-unwrap-failed') setPrfFailed(true);
        setError(err.message);
      } else {
        setError(err?.message || 'Could not unlock chat. Please try again.');
      }
      setBusy(false);
    }
  };

  const handleRecovery = async (event: React.FormEvent) => {
    event.preventDefault();
    if (busy || !recoveryInput) return;
    if (!keys.wrappedPrivateKey) {
      setError('No recovery key is registered for this account.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const result = await unwrapWithRecoveryKey(keys.wrappedPrivateKey, recoveryInput);
      if (result.status === 'mismatch') {
        setError(RECOVERY_KEY_MISMATCH_MESSAGE);
        setBusy(false);
        return;
      }
      await finishUnlock(result.privateKey, 'recovery');
    } catch (err: any) {
      setError(err?.message || 'Could not unlock chat. Please try again.');
      setBusy(false);
    }
  };

  return (
    <Paper
      elevation={0}
      className="ph-no-capture"
      data-testid="unlock-gate-root"
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 3,
        p: 4,
        maxWidth: 440,
        mx: 'auto',
        my: 6,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 1.5,
        textAlign: 'center',
        bgcolor: 'background.paper',
      }}
    >
      <Box sx={{ color: 'primary.main', display: 'inline-flex' }}>
        <KeyRound size={32} aria-hidden />
      </Box>
      <Typography variant="h6" component="h2" sx={{ fontWeight: 600 }}>
        Unlock chat
      </Typography>
      <Typography variant="body2" color="text.secondary">
        This browser is not trusted yet. Unlock once with your passkey and chat opens silently here
        for the next 30 days.
      </Typography>
      {notice && (
        <Alert severity="warning" sx={{ textAlign: 'left', width: '100%' }}>
          {notice}
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ textAlign: 'left', width: '100%' }}>
          {error}
        </Alert>
      )}

      {mode === 'choose' ? (
        <Button variant="contained" onClick={handlePasskey} disabled={busy} fullWidth sx={{ minHeight: 40, mt: 1 }}>
          {busy ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : UNLOCK_WITH_PASSKEY_LABEL}
        </Button>
      ) : (
        <Box component="form" onSubmit={handleRecovery} noValidate sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <TextField
            label="Recovery key"
            value={recoveryInput}
            onChange={(event) => setRecoveryInput(event.target.value)}
            fullWidth
            size="small"
            autoFocus
            disabled={busy}
            autoComplete="off"
            inputProps={{ spellCheck: false }}
            helperText="The 32 characters from StudySync-chat-recovery-key.txt"
          />
          <Button type="submit" variant="contained" disabled={!recoveryInput || busy} sx={{ minHeight: 40 }}>
            {busy ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Unlock'}
          </Button>
        </Box>
      )}

      {mode === 'choose' ? (
        <Button
          size="small"
          onClick={() => {
            setMode('recovery');
            setError(null);
          }}
          disabled={busy}
          sx={{ textTransform: 'none', mt: 0.5 }}
        >
          {USE_RECOVERY_KEY_LABEL}
        </Button>
      ) : (
        <Button
          size="small"
          onClick={() => {
            setMode('choose');
            setError(null);
          }}
          disabled={busy}
          sx={{ textTransform: 'none', mt: 0.5 }}
        >
          Back to passkey
        </Button>
      )}
      <Button
        size="small"
        onClick={() => setResetOpen(true)}
        disabled={busy}
        sx={{ color: 'text.secondary', textTransform: 'none', fontSize: 12 }}
      >
        {REPLACE_KEY_LABEL}
      </Button>

      <AddPasskeyDialog
        open={offerOpen}
        uid={uid}
        email={email}
        prfSalt={currentKeys.prfSalt}
        passkeyCount={currentKeys.passkeys.length}
        title={PASSKEY_OFFER_TITLE}
        intro={PASSKEY_OFFER_INTRO}
        onClose={(added) => {
          setOfferOpen(false);
          const next = added ? { ...currentKeys, passkeys: [...currentKeys.passkeys, added] } : currentKeys;
          setCurrentKeys(next);
          onUnlocked(next);
        }}
      />
      <IdentityResetDialog
        open={resetOpen}
        uid={uid}
        email={email}
        onClose={() => setResetOpen(false)}
        onComplete={(nextKeys) => {
          setResetOpen(false);
          onUnlocked(nextKeys);
        }}
      />
    </Paper>
  );
}
